/** MIME + extension allowlist for product catalog source uploads. */

export const PRODUCT_SOURCE_XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
export const PRODUCT_SOURCE_XLSM_MIME =
  'application/vnd.ms-excel.sheet.macroEnabled.12'
export const PRODUCT_SOURCE_CSV_MIME = 'text/csv'
export const PRODUCT_SOURCE_XLS_MIME = 'application/vnd.ms-excel'

export const PRODUCT_SOURCE_FILE_ACCEPT =
  'application/pdf,image/jpeg,image/png,image/bmp,' +
  `${PRODUCT_SOURCE_XLSX_MIME},${PRODUCT_SOURCE_XLSM_MIME},${PRODUCT_SOURCE_XLS_MIME},${PRODUCT_SOURCE_CSV_MIME},` +
  '.pdf,.jpg,.jpeg,.png,.bmp,.xlsx,.xlsm,.xls,.csv'

const PRODUCT_SOURCE_ALLOWED_CONTENT_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/bmp',
  'image/x-ms-bmp',
  PRODUCT_SOURCE_XLSX_MIME,
  PRODUCT_SOURCE_XLSM_MIME,
  PRODUCT_SOURCE_XLS_MIME,
  PRODUCT_SOURCE_CSV_MIME,
  'application/csv',
])

function isIgnorableBrowserContentType(contentType: string): boolean {
  const normalized = contentType.trim().toLowerCase()
  return normalized.length === 0 || normalized === 'application/octet-stream'
}

export function isProductSourceSpreadsheetFile(file: File): boolean {
  const lowerCaseFileName = file.name.toLowerCase()
  return (
    file.type === PRODUCT_SOURCE_XLSX_MIME ||
    file.type === PRODUCT_SOURCE_XLSM_MIME ||
    file.type === PRODUCT_SOURCE_XLS_MIME ||
    lowerCaseFileName.endsWith('.xlsx') ||
    lowerCaseFileName.endsWith('.xlsm') ||
    lowerCaseFileName.endsWith('.xls')
  )
}

/** XLSX, XLSM, or CSV — used for spreadsheet-specific failure copy (EXCEL-03). */
export function isProductSourceSpreadsheetOrCsvFile(file: File): boolean {
  const lowerCaseFileName = file.name.toLowerCase()
  return (
    isProductSourceSpreadsheetFile(file) ||
    file.type === PRODUCT_SOURCE_CSV_MIME ||
    file.type === 'application/csv' ||
    lowerCaseFileName.endsWith('.csv')
  )
}

export function isProductSourceSpreadsheetContentType(
  contentType: string | null | undefined
): boolean {
  if (contentType == null) {
    return false
  }
  const normalized = contentType.trim().toLowerCase()
  return (
    normalized === PRODUCT_SOURCE_XLSX_MIME ||
    normalized === PRODUCT_SOURCE_XLSM_MIME ||
    normalized === PRODUCT_SOURCE_XLS_MIME ||
    normalized === PRODUCT_SOURCE_CSV_MIME ||
    normalized === 'application/csv'
  )
}

export function isProductSourceSpreadsheetFileName(
  fileName: string | null | undefined
): boolean {
  if (fileName == null) {
    return false
  }
  const lower = fileName.trim().toLowerCase()
  return (
    lower.endsWith('.xlsx') ||
    lower.endsWith('.xlsm') ||
    lower.endsWith('.xls') ||
    lower.endsWith('.csv')
  )
}

export function isAllowedProductSourceFile(file: File): boolean {
  const lowerCaseFileName = file.name.toLowerCase()
  return (
    file.type === 'application/pdf' ||
    file.type === 'image/jpeg' ||
    file.type === 'image/png' ||
    file.type === 'image/bmp' ||
    file.type === PRODUCT_SOURCE_XLSX_MIME ||
    file.type === PRODUCT_SOURCE_XLSM_MIME ||
    file.type === PRODUCT_SOURCE_XLS_MIME ||
    file.type === PRODUCT_SOURCE_CSV_MIME ||
    file.type === 'application/csv' ||
    lowerCaseFileName.endsWith('.pdf') ||
    lowerCaseFileName.endsWith('.jpg') ||
    lowerCaseFileName.endsWith('.jpeg') ||
    lowerCaseFileName.endsWith('.png') ||
    lowerCaseFileName.endsWith('.bmp') ||
    lowerCaseFileName.endsWith('.xlsx') ||
    lowerCaseFileName.endsWith('.xlsm') ||
    lowerCaseFileName.endsWith('.xls') ||
    lowerCaseFileName.endsWith('.csv')
  )
}

function resolveProductSourceContentTypeFromExtension(fileName: string): string {
  const lowerCaseFileName = fileName.toLowerCase()
  if (lowerCaseFileName.endsWith('.xlsx')) {
    return PRODUCT_SOURCE_XLSX_MIME
  }
  if (lowerCaseFileName.endsWith('.xlsm')) {
    return PRODUCT_SOURCE_XLSM_MIME
  }
  if (lowerCaseFileName.endsWith('.xls')) {
    return PRODUCT_SOURCE_XLS_MIME
  }
  if (lowerCaseFileName.endsWith('.csv')) {
    return PRODUCT_SOURCE_CSV_MIME
  }
  if (lowerCaseFileName.endsWith('.png')) {
    return 'image/png'
  }
  if (lowerCaseFileName.endsWith('.bmp')) {
    return 'image/bmp'
  }
  if (
    lowerCaseFileName.endsWith('.jpg') ||
    lowerCaseFileName.endsWith('.jpeg')
  ) {
    return 'image/jpeg'
  }
  return 'application/pdf'
}

/** Prefer browser MIME when allowlisted; otherwise resolve from extension. */
export function resolveProductSourceContentType(file: File): string {
  const lowerCaseFileName = file.name.toLowerCase()
  const normalizedType = file.type.trim().toLowerCase()
  if (
    normalizedType === 'image/x-ms-bmp' &&
    lowerCaseFileName.endsWith('.bmp')
  ) {
    return file.type
  }
  if (
    normalizedType === PRODUCT_SOURCE_XLS_MIME &&
    !lowerCaseFileName.endsWith('.xls')
  ) {
    return resolveProductSourceContentTypeFromExtension(file.name)
  }
  if (
    !isIgnorableBrowserContentType(file.type) &&
    PRODUCT_SOURCE_ALLOWED_CONTENT_TYPES.has(normalizedType)
  ) {
    return file.type
  }
  return resolveProductSourceContentTypeFromExtension(file.name)
}
